import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Input, InputNumber, Segmented } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../../config';
import {
  NdoePage, NdoePageHeader, NdoeFormSection, NdoeActionButton,
  NdoeImage, NdoeStats, NdoeStatCard, NdoeStatusBadge, NdoeLoading, NdoeError,
} from '../../components/ndoe';
import './receptionArrivage.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

const money = (v) => `${(Math.round(Number(v || 0) * 100) / 100).toLocaleString('fr-FR')} $`;
const round2 = (v) => Math.round(v * 100) / 100;

// Miroir CLIENT de la répartition serveur (aperçu seulement : le serveur recalcule
// et fait foi). Lignes avec `cout_manuel` protégées ; la facture va sur les autres.
const repartir = ({ lignes, prixVente, montantMarchandise, montantFrais, cleMarchandise, cleFrais }) => {
  const cibles = lignes.filter((l) => l.cout_manuel === null || l.cout_manuel === undefined);
  const poids = (cle, l) => (cle === 'quantite' ? l.quantite : l.quantite * prixVente);
  const totM = cibles.reduce((s, l) => s + poids(cleMarchandise, l), 0);
  const totF = cibles.reduce((s, l) => s + poids(cleFrais, l), 0);
  return lignes.map((l) => {
    if (l.cout_manuel !== null && l.cout_manuel !== undefined) {
      return { cout: round2(Number(l.cout_manuel)), source: 'saisi' };
    }
    const cm = montantMarchandise > 0 && totM > 0 ? (montantMarchandise * poids(cleMarchandise, l)) / totM / l.quantite : 0;
    const cf = montantFrais > 0 && totF > 0 ? (montantFrais * poids(cleFrais, l)) / totF / l.quantite : 0;
    const c = round2(cm + cf);
    return { cout: c > 0 ? c : null, source: c > 0 ? 'reparti' : null };
  });
};

export default function ReceptionArrivage() {
  const { id } = useParams();                 // id_varianteProduit d'entrée (depuis le catalogue)
  const navigate = useNavigate();
  const user = useSelector((s) => s.user?.currentUser);
  const role = String(user?.role || '').toLowerCase();
  const allowed = ['admin', 'secretaire'].includes(role);

  const [fiche, setFiche] = useState(null);   // produit + variante (source du contexte)
  const [tailles, setTailles] = useState([]); // pointures du pays
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [fini, setFini] = useState(null);     // récap après enregistrement

  // Lignes de l'arrivage : { id_taille, taille, quantite, cout_manuel }
  const [lignes, setLignes] = useState([]);
  // Coût : mode + facture. Les CLÉS de répartition viennent des paramètres serveur
  // (décision : marchandise au prorata du prix de vente, frais des quantités).
  const [modeCout, setModeCout] = useState('total');
  const [moyen, setMoyen] = useState(null);
  const [facture, setFacture] = useState({ montant_marchandise: null, montant_frais: null, reference: '', commentaire: '' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/produit/varianteDetail/${id}`);
        const f = Array.isArray(data) ? data[0] : data;
        if (cancelled || !f) { setError('Variante introuvable.'); setLoading(false); return; }
        setFiche(f);
        const { data: ts } = await axios.get(`${DOMAIN}/api/produit/tailleOne/${f.id_pays}`);
        if (!cancelled) setTailles(ts || []);
      } catch (e) {
        if (!cancelled) setError('Impossible de charger la fiche de la variante.');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const prixVente = Number(fiche?.prix || 0);

  const toggleTaille = (t) => {
    setLignes((prev) => {
      const existe = prev.find((l) => l.id_taille === t.id_taille);
      if (existe) return prev.filter((l) => l.id_taille !== t.id_taille);
      return [...prev, { id_taille: t.id_taille, taille: t.taille, quantite: 1, cout_manuel: null }]
        .sort((a, b) => a.taille - b.taille);
    });
  };
  const setQuantite = (idTaille, q) => setLignes((prev) => prev.map((l) => (
    l.id_taille === idTaille ? { ...l, quantite: Math.max(1, Math.min(999, Math.round(Number(q) || 1))) } : l
  )));
  const setCoutManuel = (idTaille, v) => setLignes((prev) => prev.map((l) => (
    l.id_taille === idTaille ? { ...l, cout_manuel: v === null || v === undefined || v === '' ? null : Number(v) } : l
  )));
  const appliquerMoyen = () => {
    if (moyen === null || Number(moyen) < 0) return;
    setLignes((prev) => prev.map((l) => ({ ...l, cout_manuel: Number(moyen) })));
  };
  const recalculer = () => setLignes((prev) => prev.map((l) => ({ ...l, cout_manuel: null })));

  // Aperçu de la répartition + marge en direct (le bandeau nº4 de la maquette).
  const apercu = useMemo(() => {
    if (!lignes.length) return null;
    const mM = modeCout === 'total' ? Number(facture.montant_marchandise || 0) : 0;
    const mF = modeCout === 'total' ? Number(facture.montant_frais || 0) : 0;
    const couts = repartir({
      lignes, prixVente, montantMarchandise: mM, montantFrais: mF,
      cleMarchandise: 'valeur', cleFrais: 'quantite',
    });
    let paires = 0; let coutTotal = 0; let venteTotal = 0; let sansCout = 0; let negatives = 0;
    const parLigne = lignes.map((l, i) => {
      const c = couts[i];
      paires += l.quantite;
      venteTotal += l.quantite * prixVente;
      if (c.cout === null) sansCout += l.quantite;
      else {
        coutTotal += c.cout * l.quantite;
        if (prixVente - c.cout < 0) negatives += 1;
      }
      return { ...l, ...c, marge: c.cout === null ? null : round2(prixVente - c.cout) };
    });
    const marge = venteTotal - coutTotal;
    return {
      parLigne, paires, coutTotal, venteTotal, sansCout, negatives,
      marge, taux: venteTotal > 0 && coutTotal > 0 ? round2((marge / venteTotal) * 100) : null,
    };
  }, [lignes, prixVente, modeCout, facture.montant_marchandise, facture.montant_frais]);

  const enregistrer = useCallback(async () => {
    if (!fiche || !lignes.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('id_produit', fiche.id_produit);
      fd.append('id_couleur', fiche.id_couleur);
      fd.append('id_pays', fiche.id_pays);
      fd.append('code_variant', fiche.code_variant);
      fd.append('prix', prixVente);
      fd.append('img', fiche.img || '');
      if (facture.reference) fd.append('reference', facture.reference);
      if (facture.commentaire) fd.append('commentaire', facture.commentaire);
      if (modeCout === 'total') {
        if (facture.montant_marchandise) fd.append('montant_marchandise', facture.montant_marchandise);
        if (facture.montant_frais) fd.append('montant_frais', facture.montant_frais);
      }
      fd.append('lignes', JSON.stringify(lignes.map((l) => ({
        id_taille: l.id_taille, quantite: l.quantite, cout_manuel: l.cout_manuel,
      }))));

      const { data } = await axios.post(`${DOMAIN}/api/produit/reception/lot`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFini(data);
      toast.success(`Arrivage enregistré — ${lignes.reduce((s, l) => s + l.quantite, 0)} paire(s).`);
    } catch (e) {
      toast.error(e.response?.data?.error || "L'enregistrement de l'arrivage a échoué.");
    } finally { setBusy(false); }
  }, [fiche, lignes, prixVente, modeCout, facture]);

  if (!allowed) {
    return (
      <NdoePage>
        <NdoeError title="Accès réservé" text="La réception de stock est réservée à l'administration et au secrétariat." />
      </NdoePage>
    );
  }
  if (loading) return <NdoePage><NdoeLoading rows={4} /></NdoePage>;
  if (error || !fiche) {
    return <NdoePage><NdoeError text={error || 'Variante introuvable.'} onRetry={() => window.location.reload()} /></NdoePage>;
  }

  // --- Écran de succès : récap de l'arrivage --------------------------------
  if (fini) {
    return (
      <NdoePage>
        <NdoePageHeader eyebrow="Réception" title="Arrivage enregistré"
          subtitle={`Lot nº${fini.id_lot}${facture.reference ? ` · facture ${facture.reference}` : ''}`} />
        <div className="ra-succes">
          <CheckCircleOutlined className="ra-succes__icone" />
          <p>
            <b>{(fini.lignes || []).reduce((s, l) => s + l.quantite, 0)} paire(s)</b> ajoutée(s) au stock
            en une seule opération{(fini.lignes || []).some((l) => l.cout_source)
              ? ', coût d\'achat enregistré.' : ' — sans coût (complétable plus tard).'}
          </p>
          <div className="ra-succes__actions">
            <NdoeActionButton variant="primary" onClick={() => navigate('/reception')}>Voir les réceptions</NdoeActionButton>
            <NdoeActionButton variant="secondary" onClick={() => { setFini(null); setLignes([]); }}>
              Nouvel arrivage sur cette variante
            </NdoeActionButton>
          </div>
        </div>
      </NdoePage>
    );
  }

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Réception"
        title="Réception d'un arrivage"
        subtitle="Pointures, quantités et coût d'achat — enregistrés en une seule opération."
      />

      {/* Contexte produit : la photo d'abord, comme partout. */}
      <div className="ra-fiche">
        <NdoeImage src={fiche.img} alt={fiche.nom_produit} size="lg" />
        <div className="ra-fiche__txt">
          <b>{fiche.nom_produit}</b>
          <small>{[fiche.nom_marque, fiche.nom_categorie, fiche.description].filter(Boolean).join(' · ')}</small>
          <small className="ra-fiche__code">{fiche.code_variant} · prix de vente {money(prixVente)}</small>
        </div>
      </div>

      <NdoeFormSection step={1} title="Pointures reçues" hint="Touche une pointure pour l'ajouter, ajuste la quantité.">
        <div className="ra-tailles">
          {tailles.map((t) => {
            const active = lignes.some((l) => l.id_taille === t.id_taille);
            return (
              <button key={t.id_taille} type="button"
                className={`ra-taille ${active ? 'is-active' : ''}`}
                onClick={() => toggleTaille(t)}>
                P{t.taille}
              </button>
            );
          })}
        </div>
        {lignes.length > 0 && (
          <div className="ra-lignes">
            {lignes.map((l) => (
              <div className="ra-ligne" key={l.id_taille}>
                <span className="ra-ligne__taille">P{l.taille}</span>
                <div className="ra-ligne__qte">
                  <button type="button" aria-label="Moins" onClick={() => setQuantite(l.id_taille, l.quantite - 1)}>−</button>
                  <InputNumber inputMode="decimal" min={1} max={999} value={l.quantite} controls={false}
                    onChange={(v) => setQuantite(l.id_taille, v)} />
                  <button type="button" aria-label="Plus" onClick={() => setQuantite(l.id_taille, l.quantite + 1)}>+</button>
                </div>
                {modeCout === 'ligne' && (
                  <InputNumber inputMode="decimal" className="ra-ligne__cout" min={0} placeholder="Coût $"
                    value={l.cout_manuel} onChange={(v) => setCoutManuel(l.id_taille, v)} />
                )}
                {apercu && modeCout !== 'ligne' && (() => {
                  const p = apercu.parLigne.find((x) => x.id_taille === l.id_taille);
                  if (!p || p.cout === null) return <span className="ra-ligne__info">coût —</span>;
                  return (
                    <span className={`ra-ligne__info ${p.marge < 0 ? 'is-neg' : ''}`}>
                      {money(p.cout)}{p.source === 'saisi' ? ' · saisi' : ''} → marge {money(p.marge)}
                    </span>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </NdoeFormSection>

      <NdoeFormSection step={2} title="Coût d'achat (optionnel)"
        hint="Sans coût, l'arrivage s'enregistre quand même — il restera complétable dans « Coûts d'achat ».">
        <Segmented
          value={modeCout}
          onChange={(v) => setModeCout(v)}
          options={[
            { value: 'total', label: 'Répartir un montant' },
            { value: 'moyen', label: 'Coût moyen partout' },
            { value: 'ligne', label: 'Ligne par ligne' },
          ]}
        />
        {modeCout === 'total' && (
          <div className="ra-facture">
            <div className="ra-champ">
              <label>Marchandise ($)</label>
              <InputNumber inputMode="decimal" min={0} value={facture.montant_marchandise}
                onChange={(v) => setFacture((f) => ({ ...f, montant_marchandise: v }))} />
            </div>
            <div className="ra-champ">
              <label>Frais annexes ($)</label>
              <InputNumber inputMode="decimal" min={0} value={facture.montant_frais}
                onChange={(v) => setFacture((f) => ({ ...f, montant_frais: v }))} />
            </div>
            <p className="ra-note">
              Marchandise répartie au prorata du <b>prix de vente</b>, frais au prorata des <b>quantités</b>
              (transport, douane). Une ligne corrigée à la main est protégée —
              <button type="button" className="ra-lien" onClick={recalculer}>tout recalculer</button>.
            </p>
          </div>
        )}
        {modeCout === 'moyen' && (
          <div className="ra-facture">
            <div className="ra-champ">
              <label>Coût unitaire ($)</label>
              <InputNumber inputMode="decimal" min={0} value={moyen} onChange={setMoyen} />
            </div>
            <NdoeActionButton variant="secondary" size="sm" onClick={appliquerMoyen}>
              Appliquer à toutes les lignes
            </NdoeActionButton>
          </div>
        )}
        {modeCout === 'ligne' && (
          <p className="ra-note">Saisis le coût de chaque pointure directement dans la liste ci-dessus.</p>
        )}

        <div className="ra-champ ra-champ--large">
          <label>Nº de facture fournisseur (optionnel)</label>
          <Input maxLength={60} value={facture.reference} placeholder="ex. FAC-2026-041"
            onChange={(e) => setFacture((f) => ({ ...f, reference: e.target.value }))} />
        </div>
      </NdoeFormSection>

      {/* Marge prévisionnelle en direct — toujours affichée dès qu'il y a des lignes. */}
      {apercu && (
        <>
          <NdoeStats className="ra-stats">
            <NdoeStatCard label="Paires" value={apercu.paires} />
            <NdoeStatCard label="Coût total" value={apercu.coutTotal > 0 ? money(apercu.coutTotal) : '—'} />
            <NdoeStatCard label="Valeur de vente" value={money(apercu.venteTotal)} />
            <NdoeStatCard label="Marge prévisionnelle" value={apercu.coutTotal > 0 ? money(apercu.marge) : '—'} gold />
            <NdoeStatCard label="Taux" value={apercu.taux === null ? '—' : `${apercu.taux} %`} />
          </NdoeStats>
          {apercu.negatives > 0 && (
            <p className="ra-alerte is-danger">
              <NdoeStatusBadge kind="custom" tone="danger" label="Marge négative" />
              {' '}{apercu.negatives} pointure(s) coûteraient plus cher que leur prix de vente. Vérifie la facture ou le prix.
            </p>
          )}
          {apercu.sansCout > 0 && apercu.coutTotal > 0 && (
            <p className="ra-alerte">
              <NdoeStatusBadge kind="custom" tone="warn" label="Incomplet" />
              {' '}{apercu.sansCout} paire(s) resteront sans coût.
            </p>
          )}
        </>
      )}

      <div className="ra-submit">
        <NdoeActionButton variant="primary" block loading={busy}
          disabled={!lignes.length}
          onClick={enregistrer}>
          Enregistrer l'arrivage ({lignes.reduce((s, l) => s + l.quantite, 0)} paire{lignes.reduce((s, l) => s + l.quantite, 0) > 1 ? 's' : ''})
        </NdoeActionButton>
        <small className="ra-submit__hint">
          Tout l'arrivage est enregistré en une seule opération : soit tout passe, soit rien.
        </small>
      </div>
    </NdoePage>
  );
}
